import FilterListIcon from '@mui/icons-material/FilterList';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Typography from '@mui/material/Typography';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import * as React from 'react';

// Corrigir ícones do Leaflet
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';

// Configurar ícones padrão
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});

const LocationMap = React.memo(function LocationMap({
  locations,
  drivers,
  carriers,
  selectedDriver,
  selectedCarrier,
  onDriverChange,
  onCarrierChange,
  maps = {},
  defaultLocation = { lat: -23.550520, lng: -46.633308 },
  height = '500px',
  followedPoint = null,
  shouldFollowPoint = false
}) {
  const mapRef = React.useRef(null);
  const leafletMapRef = React.useRef(null);
  const markersRef = React.useRef([]);
  const hasInitializedBounds = React.useRef(false);
  const previousFilters = React.useRef({ driver: '', carrier: '' });
  const [selectedMapProvider, setSelectedMapProvider] = React.useState('');

  // Determinar provedores de mapa disponíveis
  const availableProviders = React.useMemo(() => {
    const providers = [];

    if (maps.here?.enabled) {
      providers.push({
        id: 'here',
        name: 'HERE Maps',
        priority: 1
      });
    }

    if (maps.open_street_map?.enabled) {
      providers.push({
        id: 'openstreetmap',
        name: 'OpenStreetMap',
        priority: 2
      });
    }

    return providers.sort((a, b) => a.priority - b.priority);
  }, [maps]);

  // Definir provedor padrão (HERE tem prioridade)
  React.useEffect(() => {
    if (availableProviders.length > 0 && !selectedMapProvider) {
      setSelectedMapProvider(availableProviders[0].id);
    }
  }, [availableProviders, selectedMapProvider]);

  // Função para criar tile layer baseado no provedor
  const createTileLayer = React.useCallback((provider) => {
    switch (provider) {
      case 'here':
        return L.tileLayer('https://maps.hereapi.com/v3/base/mc/{z}/{x}/{y}/png8?style=explore.day&apiKey=' + maps.here.api_key, {
          attribution: '© HERE Maps',
          maxZoom: 19,
        });
      case 'openstreetmap':
        return L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
          attribution: '© OpenStreetMap contributors',
          maxZoom: 19,
        });
      default:
        return null;
    }
  }, [maps]);

  // Função para limpar marcadores existentes
  const clearMarkers = React.useCallback(() => {
    markersRef.current.forEach(marker => {
      leafletMapRef.current.removeLayer(marker);
    });
    markersRef.current = [];
  }, []);

  // Função para centralizar o mapa no ponto seguido
  const centerOnFollowedPoint = React.useCallback((point) => {
    if (leafletMapRef.current && point) {
      leafletMapRef.current.setView([point.lat, point.lng], leafletMapRef.current.getZoom(), {
        animate: true,
        duration: 1 // 1 segundo de animação
      });
    }
  }, []);

  // Função para criar marcador com estilo especial para ponto seguido
  const createMarker = React.useCallback((location, isFollowed = false) => {
    let marker;

    const lastUpdate = Math.floor((Date.now() - new Date(location.created_at).getTime()) / 60000);
    const isUpdated = lastUpdate >= 0 && lastUpdate <= 15;

    if (isFollowed) {
      // Marcador especial para ponto seguido (cor diferente)
      const followedIcon = L.divIcon({
        html: `
          <div style="
            background-color: ${isUpdated ? '#1976d2' : '#ff4444'}; 
            width: 28px; 
            height: 28px; 
            border-radius: 50%; 
            border: 3px solid #fff;
            box-shadow: 0 2px 6px rgba(0,0,0,0.3);
            display: flex;
            align-items: center;
            justify-content: center;
          ">
            <div style="
              width: 18px; 
              height: 18px; 
              background-color: #fff; 
              border-radius: 50%;
              animation: ${isUpdated ? 'pulse 2s infinite' : 'none'};
            "></div>
          </div>
          <style>
            @keyframes pulse {
              0% { transform: scale(1); opacity: 1; }
              50% { transform: scale(1.2); opacity: 0.7; }
              100% { transform: scale(1); opacity: 1; }
            }
          </style>
        `,
        className: 'followed-marker',
        iconSize: [26, 26],
        iconAnchor: [13, 13]
      });

      marker = L.marker([location.lat, location.lng], { icon: followedIcon });
    } else {
      // Marcador padrão
      marker = L.marker([location.lat, location.lng]);
    }

    return marker.bindPopup(`
      <div>
        <strong>${location.driver}</strong><br>
        Velocidade: ${location.speed || 0} km/h<br>
        Direção: ${location.direction}<br>
        Precisão: ${location.accuracy}m<br>
        ${isUpdated ? `Atualizado há ${lastUpdate} min` : 'Sem atualizações nos últimos 15 minutos'}
      </div>
    `);
  }, []);

  // Inicializar o mapa Leaflet (apenas uma vez)
  React.useEffect(() => {
    if (!mapRef.current || !selectedMapProvider) return;

    try {
      // Criar o mapa apenas se não existir
      if (!leafletMapRef.current) {
        leafletMapRef.current = L.map(mapRef.current).setView([defaultLocation.lat, defaultLocation.lng], 12);
      }

      // Remover apenas a tile layer (não os marcadores)
      leafletMapRef.current.eachLayer((layer) => {
        if (layer instanceof L.TileLayer) {
          leafletMapRef.current.removeLayer(layer);
        }
      });

      // Adicionar nova tile layer
      const tileLayer = createTileLayer(selectedMapProvider);
      if (tileLayer) {
        tileLayer.addTo(leafletMapRef.current);
      }

    } catch (error) {
      console.error('Erro ao inicializar mapa:', error);
    }

    // Cleanup quando o componente for desmontado
    return () => {
      if (leafletMapRef.current) {
        leafletMapRef.current.remove();
        leafletMapRef.current = null;
      }
    };
  }, [selectedMapProvider, createTileLayer, defaultLocation]);

  // Atualizar apenas os marcadores quando locations ou filtros mudam
  React.useEffect(() => {
    if (!leafletMapRef.current) return;

    try {
      // Limpar marcadores existentes
      clearMarkers();

      // Verificar se os filtros mudaram
      const filtersChanged =
        previousFilters.current.driver !== selectedDriver ||
        previousFilters.current.carrier !== selectedCarrier;

      // Atualizar referência dos filtros
      previousFilters.current = { driver: selectedDriver, carrier: selectedCarrier };

      // Se não há localizações, resetar flag de bounds
      if (locations.length === 0) {
        hasInitializedBounds.current = false;
      }

      // Adicionar novos markers
      if (locations.length > 0) {
        const bounds = [];

        locations.forEach((location) => {
          // Verificar se este é o ponto seguido (comparar por user_id)
          const isFollowed = shouldFollowPoint && followedPoint &&
            location.user_id === followedPoint.user_id;

          const marker = createMarker(location, isFollowed)
            .addTo(leafletMapRef.current);

          markersRef.current.push(marker);
          bounds.push([location.lat, location.lng]);
        });

        // Ajustar visualização para mostrar todos os markers:
        // - Na primeira vez (inicialização)
        // - Quando os filtros mudaram
        const shouldFitBounds =
          !hasInitializedBounds.current ||
          filtersChanged;

        if (bounds.length > 0 && shouldFitBounds) {
          leafletMapRef.current.fitBounds(bounds, { padding: [50, 50] });
          hasInitializedBounds.current = true;
        }
      }
    } catch (error) {
      console.error('Erro ao atualizar marcadores:', error);
    }
  }, [locations, selectedDriver, selectedCarrier, clearMarkers, shouldFollowPoint, followedPoint, createMarker]);

  // Efeito para centralizar o mapa no ponto seguido quando ele for atualizado
  React.useEffect(() => {
    if (shouldFollowPoint && followedPoint) {
      centerOnFollowedPoint(followedPoint);
    }
  }, [shouldFollowPoint, followedPoint, centerOnFollowedPoint]);

  // Redimensionar mapa quando o container muda
  React.useEffect(() => {
    if (leafletMapRef.current) {
      setTimeout(() => {
        leafletMapRef.current.invalidateSize();
      }, 100);
    }
  }, [height]);

  // Se nenhum provedor está disponível, mostrar mensagem
  if (availableProviders.length === 0) {
    return (
      <Card elevation={2} sx={{ mb: 3 }}>
        <CardContent>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: height,
              bgcolor: 'grey.50'
            }}
          >
            <Alert severity="warning" sx={{ maxWidth: 500 }}>
              <Typography variant="h6" gutterBottom>
                Nenhum mapa disponível
              </Typography>
              <Typography variant="body2">
                Não há provedores de mapa habilitados para este cliente no momento.
              </Typography>
              <Typography variant="caption" sx={{ mt: 1, display: 'block' }}>
                Entre em contato com o suporte para habilitar os serviços de mapa.
              </Typography>
            </Alert>
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card elevation={2} sx={{ mb: 3 }}>
      <CardContent sx={{ p: 0 }}>
        {/* Cabeçalho do mapa */}
        <Box sx={{ pb: 2, borderBottom: 1, borderColor: 'divider' }}>
          {/* Chips com estatísticas e filtros */}
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              {/* Map Provider Selector - só aparece se houver múltiplos provedores */}
              {availableProviders.length > 1 && (
                <FormControl size="small" sx={{ minWidth: 150 }}>
                  <InputLabel>Provedor de Mapa</InputLabel>
                  <Select
                    value={selectedMapProvider}
                    label="Provedor de Mapa"
                    onChange={(e) => setSelectedMapProvider(e.target.value)}
                  >
                    {availableProviders.map((provider) => (
                      <MenuItem key={provider.id} value={provider.id}>
                        {provider.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}

              {/* Driver Filter */}
              <FormControl size="small" sx={{ minWidth: 180 }}>
                <InputLabel>Motorista</InputLabel>
                <Select
                  value={selectedDriver}
                  label="Motorista"
                  onChange={(e) => onDriverChange(e.target.value)}
                >
                  <MenuItem value="">
                    <em>Todos os motoristas</em>
                  </MenuItem>
                  {drivers.map((driver) => (
                    <MenuItem key={driver} value={driver}>
                      {driver}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Carrier Filter */}
              <FormControl size="small" sx={{ minWidth: 180 }}>
                <InputLabel>Transportadora</InputLabel>
                <Select
                  value={selectedCarrier}
                  label="Transportadora"
                  onChange={(e) => onCarrierChange(e.target.value)}
                >
                  <MenuItem value="">
                    <em>Todas as transportadoras</em>
                  </MenuItem>
                  {carriers.map(([name, id]) => (
                    <MenuItem key={id} value={id}>
                      {name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Clear Filters Button */}
              {
                (selectedDriver || selectedCarrier) && (
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<FilterListIcon fontSize="small" />}
                    onClick={() => {
                      onDriverChange('');
                      onCarrierChange('');
                    }}
                    sx={{
                      minWidth: 'fit-content',
                      color: 'text.secondary',
                      borderColor: 'divider'
                    }}
                  >
                    Limpar Filtros
                  </Button>
                )
              }

            </Box>

            <Box sx={{ flexGrow: 1 }} />

            <Chip
              label={`${locations.length} localizaç${locations.length !== 1 ? 'ões' : 'ão'}`}
              color="primary"
              size="small"
            />
            <Chip
              label={`${drivers.length} motorista${drivers.length !== 1 ? 's' : ''}`}
              variant="outlined"
              size="small"
            />

            {/* Indicador do provedor ativo */}
            {selectedMapProvider && selectedMapProvider === 'here' && (
              <Box
                component="img"
                src="/here-logo.svg"
                alt="HERE Maps"
                sx={{
                  width: 28,
                  height: 28,
                  mx: 2,
                  display: 'block'
                }}
              />
            )}
          </Box>
        </Box>

        {/* Mapa */}
        <Box sx={{ position: 'relative', height }}>
          {locations.length === 0 ? (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                bgcolor: 'grey.50'
              }}
            >
              <Alert severity="info" sx={{ maxWidth: 400 }}>
                <Typography variant="body2">
                  Nenhuma localização disponível no momento.
                </Typography>
                <Typography variant="caption" sx={{ mt: 1, display: 'block' }}>
                  As localizações aparecerão aqui quando os motoristas estiverem ativos.
                </Typography>
              </Alert>
            </Box>
          ) : (
            <div
              ref={mapRef}
              style={{
                height: '100%',
                width: '100%',
                zIndex: 1
              }}
            />
          )}
        </Box>
      </CardContent>
    </Card>
  );
});

export default LocationMap; 